import React, { useEffect, useState } from 'react';
import { Loader2, FileWarning } from 'lucide-react';
import { getRazorpayBridge, getBankCoverage, inr } from '../data';
import type { RazorpayBridge, BankCoverage } from '../types';
import { StatementUpload } from './StatementUpload';
import { BankCrosscheck } from './BankCrosscheck';

/** Bank leg: Razorpay funding bridge + statement coverage, both with caveats shown. */
export const BankBridge: React.FC = () => {
	const [rb, setRb] = useState<RazorpayBridge | null>(null);
	const [cov, setCov] = useState<BankCoverage | null>(null);
	const [err, setErr] = useState<string | null>(null);

	const [reloadKey, setReloadKey] = useState(0);

	useEffect(() => {
		let on = true;
		Promise.all([getRazorpayBridge(), getBankCoverage()])
			.then(([a, b]) => {
				if (!on) return;
				setRb(a);
				setCov(b);
			})
			.catch(e => on && setErr(e?.message || 'Failed'));
		return () => {
			on = false;
		};
	}, [reloadKey]);

	if (err) return <div className='p-6 text-red-600'>Error: {err}</div>;
	if (!rb || !cov)
		return (
			<div className='flex items-center gap-2 p-6 text-gray-500'>
				<Loader2 className='animate-spin' size={18} /> Loading bank reconciliation…
			</div>
		);

	const maxAbs = Math.max(...rb.months.map(m => Math.max(m.net_funded, m.disbursed)), 1);

	return (
		<div className='space-y-6'>
			<BankCrosscheck />

			<StatementUpload onUploaded={() => setReloadKey(k => k + 1)} />

			{cov.documentsNeeded && cov.documentsNeeded.length > 0 && (
				<section className='bg-amber-50 rounded-lg border border-amber-200 p-5'>
					<div className='flex items-center gap-2 mb-2'>
						<FileWarning size={18} className='text-amber-600' />
						<h3 className='font-semibold text-amber-900'>Statements still needed</h3>
					</div>
					<div className='space-y-2'>
						{cov.documentsNeeded.map(d => (
							<div
								key={d.document}
								className='flex items-center justify-between text-sm bg-white rounded px-3 py-2 border border-amber-100'>
								<span className='text-gray-800'>{d.document}</span>
								<span className='text-gray-500'>
									{d.unverified_payments} payments · {inr(d.unverified_value)} unverified
								</span>
							</div>
						))}
					</div>
					<p className='text-xs text-amber-700/70 mt-2'>
						Upload any of these above to close the gap — matching runs automatically.
					</p>
				</section>
			)}

			<section className='bg-white rounded-lg border border-gray-200 p-5'>
				<div className='flex items-baseline justify-between mb-1'>
					<h3 className='font-semibold text-gray-900'>Razorpay funding bridge</h3>
					<span className='text-sm text-gray-500'>ICICI → RazorpayX (no RZP statement)</span>
				</div>
				<div className='grid grid-cols-3 gap-3 my-4'>
					<Tile label='Funded in (ICICI)' value={inr(rb.totals.net_funded)} tone='blue' />
					<Tile label='Disbursed (Zoho)' value={inr(rb.totals.disbursed)} tone='gray' />
					<Tile label='Unexplained' value={inr(rb.totals.unexplained)} tone='red' />
				</div>

				<div className='space-y-1.5 mt-4'>
					{rb.months.map(m => (
						<div key={m.month} className='flex items-center gap-3 text-xs'>
							<span className='w-16 text-gray-500'>{m.month}</span>
							<div className='flex-1 flex items-center gap-1'>
								<div
									className='h-4 bg-blue-400 rounded-sm'
									style={{ width: `${(m.net_funded / maxAbs) * 100}%` }}
									title={`funded ${inr(m.net_funded)}`}
								/>
								<div
									className='h-4 bg-gray-300 rounded-sm'
									style={{ width: `${(m.disbursed / maxAbs) * 100}%` }}
									title={`disbursed ${inr(m.disbursed)}`}
								/>
							</div>
							<span
								className={`w-20 text-right ${
									Math.abs(m.gap) > 1000000 ? 'text-red-600 font-medium' : 'text-gray-400'
								}`}>
								{inr(m.gap)}
							</span>
						</div>
					))}
				</div>
				<p className='text-xs text-gray-400 mt-3'>{rb.caveat}</p>
			</section>

			<section className='bg-white rounded-lg border border-gray-200 p-5'>
				<h3 className='font-semibold text-gray-900 mb-3'>Statement coverage vs Zoho payments</h3>
				<table className='w-full text-sm'>
					<thead className='text-gray-500 text-xs uppercase'>
						<tr>
							<th className='text-left py-2'>Account</th>
							<th className='text-right py-2'>Payments</th>
							<th className='text-right py-2'>Value</th>
							<th className='text-right py-2'>No ref</th>
							<th className='text-left py-2 pl-4'>Statement</th>
						</tr>
					</thead>
					<tbody>
						{cov.paymentAccounts.map(a => (
							<tr key={a.account} className='border-t border-gray-100'>
								<td className='py-2 text-gray-800'>{a.account}</td>
								<td className='py-2 text-right'>{a.payments}</td>
								<td className='py-2 text-right'>{inr(a.value)}</td>
								<td className='py-2 text-right text-orange-600'>{a.missing_reference}</td>
								<td className='py-2 pl-4'>
									<span
										className={`px-2 py-0.5 rounded text-xs ${
											a.statement === 'full'
												? 'bg-green-100 text-green-700'
												: a.statement === 'none'
												? 'bg-red-100 text-red-700'
												: 'bg-orange-100 text-orange-700'
										}`}>
										{a.statement}
									</span>
								</td>
							</tr>
						))}
					</tbody>
				</table>
				<p className='text-xs text-gray-400 mt-3'>{cov.caveat}</p>
			</section>
		</div>
	);
};

const Tile: React.FC<{ label: string; value: string; tone: 'blue' | 'gray' | 'red' }> = ({
	label,
	value,
	tone,
}) => (
	<div
		className={`p-3 rounded-lg border ${
			tone === 'red'
				? 'bg-red-50 border-red-100'
				: tone === 'blue'
				? 'bg-blue-50 border-blue-100'
				: 'bg-gray-50 border-gray-100'
		}`}>
		<div className='text-xs text-gray-500'>{label}</div>
		<div
			className={`text-xl font-bold ${
				tone === 'red' ? 'text-red-700' : tone === 'blue' ? 'text-blue-700' : 'text-gray-900'
			}`}>
			{value}
		</div>
	</div>
);
